const API_BASE_URL = process.env.REACT_APP_API_URL ?? 'http://localhost:8000';

function authHeaders(json = false): Record<string, string> {
  const token = localStorage.getItem('auth_token');
  return {
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...(json ? { 'Content-Type': 'application/json' } : {}),
  };
}

async function asJson<T>(response: Response): Promise<T> {
  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    const detail = body.detail;
    if (typeof detail === 'string') throw new Error(detail);
    if (detail?.message) {
      const blockers = detail.blockers || detail.problems;
      throw new Error(`${detail.message}${blockers?.length ? `: ${blockers.join(', ')}` : ''}`);
    }
    throw new Error(`Request failed (${response.status})`);
  }
  return response.json();
}

export interface LibraryQueueItem {
  item_key: string;
  catalog_id: string;
  title: string;
  intake_type: string;
  collection_id: string | null;
  adapter: string;
  category: string;
  content_kind: string;
  editorial_lane: string;
  editorial_state: string;
  rights_state: string;
  story_id: string | null;
  word_count: number | null;
  estimated_duration_minutes: number | null;
  workspace_revision: number | null;
  generation_state: string;
  validation_state: string;
  reviewable?: boolean;
  queue_section?: 'in_review' | 'published' | 'candidates' | 'cleanup' | 'held';
  next_action?: string;
  text_approved?: boolean;
  missing_reviews?: string[];
  preparation_note?: string | null;
  workflow: { status: string; last_completed_step: string | null; failure_code: string | null } | null;
}

export interface ReviewState {
  status: string;
  current: boolean;
  decided_at: string | null;
  workspace_revision: number | null;
}

export interface EditorialWorkspace {
  id: string;
  version: number;
  revision: number;
  editorial_brief: string;
  source_manifest: Record<string, unknown>;
  source_excerpt: string;
  sensitivity_review: Record<string, unknown>;
  story_text: string;
  performance_document: Record<string, unknown>;
  artwork_asset_key: string | null;
  artwork_checksum_sha256: string | null;
  artwork_rights: Record<string, unknown>;
  artwork_url: string | null;
  generation_state: string;
  validation_state: string;
  validation_report: any;
  reviews: Record<string, ReviewState>;
  review_blockers: string[];
  word_count: number;
  estimated_duration_minutes: number | null;
  generated_at: string | null;
  updated_at: string | null;
}

export interface LibraryAdminDetail extends LibraryQueueItem {
  workspace: EditorialWorkspace;
  publication_blockers: string[];
  immutable_review: Record<string, string | boolean | null>;
}

export async function getLibraryAdminSession() {
  return asJson<{ is_internal_operator: boolean; generation_enabled: boolean; text_model: string; image_model: string }>(
    await fetch(`${API_BASE_URL}/admin/library/session`, { headers: authHeaders() })
  );
}

export async function fetchLibraryQueue(filters: { search?: string; state?: string; category?: string; intake_type?: string } = {}) {
  const params = new URLSearchParams();
  Object.entries(filters).forEach(([key, value]) => value && params.set(key, value));
  const query = params.toString();
  return asJson<{ total: number; filtered: number; state_counts: Record<string, number>; items: LibraryQueueItem[] }>(
    await fetch(`${API_BASE_URL}/admin/library${query ? `?${query}` : ''}`, { headers: authHeaders() })
  );
}

export async function intakeGrimmBatch2() {
  return asJson<{ considered: number; inserted: number; existing: number; catalog_ids: string[] }>(
    await fetch(`${API_BASE_URL}/admin/library/grimm-batch-2-intake`, {
      method: 'POST', headers: authHeaders(true),
      body: JSON.stringify({ confirmation: 'ADD MOTHER HOLLE AND THE THREE FEATHERS' }),
    })
  );
}

export async function intakeGentleBatch3() {
  return asJson<{ considered: number; inserted: number; existing: number; catalog_ids: string[] }>(
    await fetch(`${API_BASE_URL}/admin/library/gentle-batch-3-intake`, {
      method: 'POST', headers: authHeaders(true),
      body: JSON.stringify({ confirmation: 'ADD ELVES AND BREMEN FOR REVIEW' }),
    })
  );
}

export async function intakeWizardOzPilot() {
  return asJson<{ parent_catalog_id: string; segment_ids: string[]; inserted_segments: number; story_rows_created: number; publication_blocker: string }>(
    await fetch(`${API_BASE_URL}/admin/library/wizard-oz-pilot-intake`, {
      method: 'POST', headers: authHeaders(true),
      body: JSON.stringify({ confirmation: 'ADD WIZARD OF OZ THREE SEGMENTS FOR REVIEW' }),
    })
  );
}

export async function createLibraryIntake(payload: {
  item_key: string; title: string; intake_type: 'original' | 'licensed';
  collection_id?: string; category?: string; editorial_lane?: string; editorial_brief: string;
}) {
  return asJson<LibraryQueueItem>(
    await fetch(`${API_BASE_URL}/admin/library`, {
      method: 'POST', headers: authHeaders(true), body: JSON.stringify(payload),
    })
  );
}

export async function fetchLibraryAdmin(catalogId: string) {
  return asJson<LibraryAdminDetail>(
    await fetch(`${API_BASE_URL}/admin/library/${catalogId}`, { headers: authHeaders() })
  );
}

export async function saveLibraryWorkspace(catalogId: string, payload: Record<string, unknown>) {
  return asJson<EditorialWorkspace>(
    await fetch(`${API_BASE_URL}/admin/library/${catalogId}/workspace`, {
      method: 'PATCH', headers: authHeaders(true), body: JSON.stringify(payload),
    })
  );
}

export async function validateLibraryWorkspace(catalogId: string) {
  return asJson<any>(
    await fetch(`${API_BASE_URL}/admin/library/${catalogId}/validate`, { method: 'POST', headers: authHeaders() })
  );
}

export async function decideLibraryStage(catalogId: string, stage: string, decision: string, notes?: string) {
  return asJson<any>(
    await fetch(`${API_BASE_URL}/admin/library/${catalogId}/decisions`, {
      method: 'POST', headers: authHeaders(true), body: JSON.stringify({ stage, decision, notes: notes || null }),
    })
  );
}

export async function uploadLibraryArtwork(catalogId: string, file: File) {
  const form = new FormData();
  form.append('file', file);
  return asJson<any>(
    await fetch(`${API_BASE_URL}/admin/library/${catalogId}/artwork`, { method: 'POST', headers: authHeaders(), body: form })
  );
}

export async function fetchLibraryArtworkBlobUrl(catalogId: string) {
  const response = await fetch(`${API_BASE_URL}/admin/library/${catalogId}/artwork`, { headers: authHeaders() });
  if (!response.ok) return null;
  return URL.createObjectURL(await response.blob());
}

export async function runLibraryPipeline(catalogId: string, includeArtwork = true) {
  return asJson<any>(
    await fetch(`${API_BASE_URL}/admin/library/${catalogId}/pipeline`, {
      method: 'POST', headers: authHeaders(true),
      body: JSON.stringify({ idempotency_key: `${catalogId}:${crypto.randomUUID()}`, include_artwork: includeArtwork }),
    })
  );
}

export async function promoteLibraryItem(catalogId: string) {
  return asJson<any>(
    await fetch(`${API_BASE_URL}/admin/library/${catalogId}/promote`, { method: 'POST', headers: authHeaders() })
  );
}

export async function publishLibraryItem(catalogId: string, confirmationTitle: string) {
  return asJson<any>(
    await fetch(`${API_BASE_URL}/admin/library/${catalogId}/publish`, {
      method: 'POST', headers: authHeaders(true), body: JSON.stringify({ confirmation_title: confirmationTitle }),
    })
  );
}

export interface SavedPreparation {
  language: string;
  performance_version_id: string;
  document_sha256: string;
  source_version_id: string;
  source_document_sha256: string;
  workspace_revision: number;
  preview: import('../storyPreparation/model').Preview;
  performance: { source_ref: { source_version_id: string; text_sha256: string } } & Record<string, any>;
}
export interface PreparationSave {
  save_id: string;
  expected_head: string | null;
  workspace_revision: number;
  language: string;
  preview: import('../storyPreparation/model').Preview;
}
export async function loadPreparation(itemKey: string): Promise<{saved: SavedPreparation | null}> {
  return asJson(await fetch(`${API_BASE_URL}/admin/library/${encodeURIComponent(itemKey)}/preparation`, { headers: authHeaders() }));
}
export async function savePreparation(itemKey: string, payload: PreparationSave): Promise<SavedPreparation> {
  return asJson(await fetch(`${API_BASE_URL}/admin/library/${encodeURIComponent(itemKey)}/preparation`, {
    method: 'POST', headers: authHeaders(true), body: JSON.stringify(payload),
  }));
}

export interface ReviewV2Finding { id: string; code: string; severity: 'blocking' | 'advisory'; span: [number, number]; target_ids: string[]; target_sha256: string }
export interface ReviewV2Performance {
  performance_version_id: string; schema_version: string;
  paragraphs: { id: string; span: [number, number] }[];
  segments: { id: string; span: [number, number] }[];
  passages: { id: string; segment_ids: string[] }[];
  characters: { id: string; name: string; aliases: string[] }[];
  turns: { id: string; span: [number, number]; role: string; attribution: string; character_id: string | null; evidence: string }[];
  review: { findings: ReviewV2Finding[]; decisions: { finding_id: string; action: string }[]; completion: unknown };
}
export interface ReviewV2Proposal { source: { text: string; language: string }; performance: ReviewV2Performance; base_sha256: string; head: string | null; workspace_revision: number }
export type ReviewV2Action = { action_id: string; action: string; [key: string]: unknown };
export interface ReviewV2Request { workspace_revision: number; language: string; base_performance_id: string | null; corrections: ReviewV2Action[] }
export interface ReviewV2Save extends ReviewV2Request { save_id: string; expected_head: string | null; base_sha256: string; actions: ReviewV2Action[]; complete_review: true }
export async function preparationCapabilities(item: string): Promise<{v1: boolean; v2: boolean; performance_schema: string; policy_id: string; detector_revision: string}> {
  return asJson(await fetch(`${API_BASE_URL}/admin/library/${encodeURIComponent(item)}/preparation-capabilities`, {headers: authHeaders()}));
}
export async function previewPreparationV2(item: string, payload: ReviewV2Request): Promise<ReviewV2Proposal> {
  return asJson(await fetch(`${API_BASE_URL}/admin/library/${encodeURIComponent(item)}/preparation-v2/preview`, {method:'POST',headers:authHeaders(true),body:JSON.stringify(payload)}));
}
export async function savePreparationV2(item: string, payload: ReviewV2Save): Promise<ReviewV2Proposal> {
  return asJson(await fetch(`${API_BASE_URL}/admin/library/${encodeURIComponent(item)}/preparation-v2`, {method:'POST',headers:authHeaders(true),body:JSON.stringify(payload)}));
}
